import { useSyncExternalStore } from "react";

import { DEFAULT_BRAND, IS_PRIVATE_BUILD, NEUTRAL_BRAND } from "./defaultBrand";
import type { Brand, BrandNames } from "./types";

/**
 * 品牌单例 store(模块级)。初值 = DEFAULT_BRAND:单测 / 未跑 bootstrap 的入口(mock 桩页)
 * 看到的就是直营口径,与改造前一致。bootstrapBrand 在 createRoot 之前 setBrand 一次,
 * SWR 刷新拿到不同品牌时再 set 一次。
 * ★私有化构建(VITE_PRIVATE=1)初值 = NEUTRAL:bootstrap 之前任何一帧都不许露直营字样。
 */
let current: Brand = IS_PRIVATE_BUILD ? NEUTRAL_BRAND : DEFAULT_BRAND;
const listeners = new Set<() => void>();

export function getBrand(): Brand {
  return current;
}

export function setBrand(next: Brand): void {
  if (next === current) return;
  current = next;
  listeners.forEach((l) => l());
}

export function subscribeBrand(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 「渠道不存在」标记(OEM D1):/public/brand 回 404 PARTNER_NOT_FOUND 时由 bootstrap 置位,
 * 入口的 PartnerNotFoundGate 据此只渲染中性提示页。与品牌共用同一组订阅者。
 */
let partnerNotFound = false;

export function isPartnerNotFound(): boolean {
  return partnerNotFound;
}

export function setPartnerNotFound(next: boolean): void {
  if (next === partnerNotFound) return;
  partnerNotFound = next;
  listeners.forEach((l) => l());
}

export function usePartnerNotFound(): boolean {
  return useSyncExternalStore(subscribeBrand, isPartnerNotFound, isPartnerNotFound);
}

/** React 读品牌(品牌变化会重渲)。非组件代码用 getBrand()。 */
export function useBrand(): Brand {
  return useSyncExternalStore(subscribeBrand, getBrand, getBrand);
}

/** 按语言取名称字段(`en*` → locales.en 覆写;缺字段回落中文)。 */
export function localizedNames(brand: Brand, lng?: string): BrandNames {
  const base: BrandNames = {
    name: brand.name,
    subtitle: brand.subtitle,
    productName: brand.productName,
    shortName: brand.shortName,
    shellName: brand.shellName,
    orgName: brand.orgName,
    copyright: brand.copyright,
    description: brand.description,
  };
  if (lng && lng.toLowerCase().startsWith("en") && brand.locales?.en) {
    return { ...base, ...brand.locales.en };
  }
  return base;
}

/** 版权行:`{year}` → 当年;空串保持空(= 不显示)。 */
export function formatCopyright(copyright: string, now: Date = new Date()): string {
  return copyright.replace(/\{year\}/g, String(now.getFullYear()));
}

/** 登录地址:品牌没给就用当前 origin。 */
export function resolveLoginOrigin(brand: Brand): string {
  if (brand.loginOrigin) return brand.loginOrigin;
  return typeof window !== "undefined" ? window.location.origin : "";
}

/**
 * i18n `interpolation.defaultVariables` 的扁平变量表(设计稿 §1 i18n 行)。
 * 文案里写 {{brandName}} / {{productName}} / {{personaTitle}} …,不再写死品牌字样。
 */
export function brandI18nVars(brand: Brand, lng?: string, now: Date = new Date()) {
  const n = localizedNames(brand, lng);
  return {
    brandName: n.name,
    brandSubtitle: n.subtitle,
    productName: n.productName,
    shortName: n.shortName,
    shellName: n.shellName,
    orgName: n.orgName,
    copyright: formatCopyright(n.copyright, now),
    personaTitle: brand.persona.title,
    personaName: brand.persona.name,
    personaFull: `${brand.persona.name}${brand.persona.title}`,
    loginOrigin: resolveLoginOrigin(brand),
  };
}

export type BrandI18nVars = ReturnType<typeof brandI18nVars>;
